import { collection, getDocs, query, where, addDoc, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { GetHousingInformationReturnType, Housing } from './types';
import { summarizeApartmentAccess } from '../utils/apartmentAccess';
import { normalizeAddress, normalizeApartmentNumber, normalizeZipCode } from '../utils/apartmentKey';

export const getHousingInformation = async (
  userId: string
): Promise<GetHousingInformationReturnType> => {
  try {
    console.log('🔍 DEBUG: getHousingInformation startar för användare:', userId);
    
    const q = query(collection(db, 'housing'), where('ownedBy', '==', userId));
    const querySnapshot = await getDocs(q);

    const data: Housing[] = [];

    querySnapshot.forEach((doc) => {
      const housingData = doc.data() as Housing;
      data.push({ id: doc.id, ...housingData } as any);

      console.log('🔍 DEBUG: Hittade lägenhet:', {
        id: doc.id,
        address: housingData.address,
        apartmentNumber: housingData.apartmentNumber,
        zipCode: housingData.zipCode,
        associationId: housingData.associationId,
        status: housingData.status
      });
    });

    // Om användaren har lägenheter utan associationId, försök matcha dem
    const unmatchedHousing = data.filter(housing => !housing.associationId || housing.associationId === '');
    
    if (unmatchedHousing.length > 0) {
      console.log('🔍 DEBUG: Hittade', unmatchedHousing.length, 'lägenheter utan förening, försöker matcha...');
      
      for (const housing of unmatchedHousing) {
        console.log('🔍 DEBUG: Försöker matcha lägenhet:', housing.address);

        // Normalisera input-värden för matchning
        const normalizedStreet = normalizeAddress(housing.address);
        const normalizedZipCode = normalizeZipCode(housing.zipCode);
        const normalizedApartmentNumber = normalizeApartmentNumber(housing.apartmentNumber);

        // Försök hitta matchande förening (sök i alla lägenheter, inte bara användarens)
        const allHousingQuery = query(collection(db, 'housing'));
        const allHousingSnapshot = await getDocs(allHousingQuery);
        
        let bestMatch: any = null;
        let bestScore = 0;

        allHousingSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const dbAddress = normalizeAddress(data.address || '');
          const dbZipCode = normalizeZipCode(data.zipCode || '');
          const dbApartmentNumber = normalizeApartmentNumber(data.apartmentNumber || '');

          // Beräkna matchningspoäng
          let score = 0;
          
          // Lägenhetsnummer match (viktigast)
          if (normalizedApartmentNumber === dbApartmentNumber) {
            score += 50;
          }
          
          // Adress match
          if (normalizedStreet === dbAddress) {
            score += 30;
          } else if (normalizedStreet.includes(dbAddress) || dbAddress.includes(normalizedStreet)) {
            score += 15;
          }
          
          // Postnummer match
          if (normalizedZipCode === dbZipCode) {
            score += 20;
          }

          // Bara matcha mot lägenheter som har associationId (från föreningar)
          if (score > bestScore && data.associationId && data.associationId !== '') {
            bestScore = score;
            bestMatch = { id: doc.id, data: data, score: score };
          }
        });
        
        if (bestMatch && bestMatch.score >= 70) { // Lägenhetsnummer (50) + postnummer (20) måste matcha
          console.log('🔍 DEBUG: Match hittad! Uppdaterar lägenhet med associationId:', bestMatch.data.associationId);
          
          // Uppdatera lägenheten med rätt associationId
          const housingRef = doc(db, 'housing', querySnapshot.docs.find(d => d.data().address === housing.address)?.id || '');
          await updateDoc(housingRef, {
            associationId: bestMatch.data.associationId,
            housingCooperative: bestMatch.data.housingCooperative || '',
            updatedAt: new Date().toISOString()
          });
          
          // Uppdatera data i return-värdet
          const index = data.findIndex(h => h.address === housing.address);
          if (index !== -1) {
            data[index].associationId = bestMatch.data.associationId;
            data[index].housingCooperative = bestMatch.data.housingCooperative || '';
          }
        } else {
          console.log('🔍 DEBUG: Ingen match hittad för lägenhet:', housing.address);
        }
      }
    } else {
      console.log('🔍 DEBUG: Alla lägenheter har redan associationId');
    }

    console.log('🔍 DEBUG: getHousingInformation avslutar med', data.length, 'lägenheter');
    return { data };
  } catch (error) {
    console.error('===getHousingInformation===', error);
    return {
      errorMessage: 'Error when fetching housing...'
    };
  }
};

// Normaliseringen bor i utils/apartmentKey — delad med importen och
// medlemsredigeringen, så samma lägenhet får samma nyckel oavsett väg in.

// Hitta matchande lägenhet baserat på PersonData
export const findMatchingHousing = async (personData: any) => {
  try {
    console.log('🔍 DEBUG: findMatchingHousing startar');
    console.log('🔍 DEBUG: PersonData input:', {
      street: personData.Basic.Street,
      zipCode: personData.Basic.ZipCode,
      apartmentNumber: personData.Basic.ApartmentNumber
    });

    // Normalisera input-värden
    const normalizedStreet = normalizeAddress(personData.Basic.Street);
    const normalizedZipCode = normalizeZipCode(personData.Basic.ZipCode);
    const normalizedApartmentNumber = normalizeApartmentNumber(personData.Basic.ApartmentNumber);

    console.log('🔍 DEBUG: Normaliserade värden:', {
      street: normalizedStreet,
      zipCode: normalizedZipCode,
      apartmentNumber: normalizedApartmentNumber
    });

    // Försök match med normaliserade fält först
    const normalizedMatchQuery = query(
      collection(db, 'housing'),
      where('normalizedAddress', '==', normalizedStreet),
      where('normalizedZipCode', '==', normalizedZipCode),
      where('normalizedApartmentNumber', '==', normalizedApartmentNumber)
    );

    console.log('🔍 DEBUG: Normaliserad match query:', {
      normalizedAddress: normalizedStreet,
      normalizedZipCode: normalizedZipCode,
      normalizedApartmentNumber: normalizedApartmentNumber
    });

    const normalizedMatchSnapshot = await getDocs(normalizedMatchQuery);
    console.log('🔍 DEBUG: Normaliserad match resultat:', normalizedMatchSnapshot.size, 'dokument hittade');

    if (!normalizedMatchSnapshot.empty) {
      const doc = normalizedMatchSnapshot.docs[0];
      console.log('🔍 DEBUG: Normaliserad match hittad:', doc.data());
      return { id: doc.id, data: doc.data() };
    }

    // Fallback: Försök exakt match på original-fält (för bakåtkompatibilitet)
    const exactMatchQuery = query(
      collection(db, 'housing'),
      where('address', '==', personData.Basic.Street),
      where('zipCode', '==', personData.Basic.ZipCode),
      where('apartmentNumber', '==', personData.Basic.ApartmentNumber)
    );

    console.log('🔍 DEBUG: Original match query (fallback):', {
      address: personData.Basic.Street,
      zipCode: personData.Basic.ZipCode,
      apartmentNumber: personData.Basic.ApartmentNumber
    });

    const exactMatchSnapshot = await getDocs(exactMatchQuery);
    console.log('🔍 DEBUG: Original match resultat:', exactMatchSnapshot.size, 'dokument hittade');

    if (!exactMatchSnapshot.empty) {
      const doc = exactMatchSnapshot.docs[0];
      console.log('🔍 DEBUG: Original match hittad:', doc.data());
      return { id: doc.id, data: doc.data() };
    }
    
    // Försök partiell match (postnummer + lägenhetsnummer)
    const partialMatchQuery = query(
      collection(db, 'housing'),
      where('zipCode', '==', personData.Basic.ZipCode),
      where('apartmentNumber', '==', personData.Basic.ApartmentNumber)
    );
    
    console.log('🔍 DEBUG: Partiell match query:', {
      zipCode: personData.Basic.ZipCode,
      apartmentNumber: personData.Basic.ApartmentNumber
    });
    
    const partialMatchSnapshot = await getDocs(partialMatchQuery);
    console.log('🔍 DEBUG: Partiell match resultat:', partialMatchSnapshot.size, 'dokument hittade');
    
    if (!partialMatchSnapshot.empty) {
      const doc = partialMatchSnapshot.docs[0];
      console.log('🔍 DEBUG: Partiell match hittad:', doc.data());
      return { id: doc.id, data: doc.data() };
    }

    // Förbättrad fallback: Hämta alla lägenheter och matcha manuellt
    console.log('🔍 DEBUG: Förbättrad fallback: Hämter alla lägenheter för manuell matchning');
    const allHousingQuery = query(collection(db, 'housing'));
    const allHousingSnapshot = await getDocs(allHousingQuery);
    
    let bestMatch: any = null;
    let bestScore = 0;

    allHousingSnapshot.docs.forEach((doc, index) => {
      const data = doc.data();
      const dbAddress = normalizeAddress(data.address || '');
      const dbZipCode = normalizeZipCode(data.zipCode || '');
      const dbApartmentNumber = normalizeApartmentNumber(data.apartmentNumber || '');

      console.log(`🔍 DEBUG: Jämför lägenhet ${index + 1}:`, {
        input: { street: normalizedStreet, zipCode: normalizedZipCode, apartmentNumber: normalizedApartmentNumber },
        db: { address: dbAddress, zipCode: dbZipCode, apartmentNumber: dbApartmentNumber }
      });

      // Beräkna matchningspoäng
      let score = 0;
      
      // Lägenhetsnummer match (viktigast)
      if (normalizedApartmentNumber === dbApartmentNumber) {
        score += 50;
        console.log(`🔍 DEBUG: Lägenhetsnummer match! +50 poäng`);
      }
      
      // Adress match
      if (normalizedStreet === dbAddress) {
        score += 30;
        console.log(`🔍 DEBUG: Adress match! +30 poäng`);
      } else if (normalizedStreet.includes(dbAddress) || dbAddress.includes(normalizedStreet)) {
        score += 15;
        console.log(`🔍 DEBUG: Delvis adress match! +15 poäng`);
      }
      
      // Postnummer match
      if (normalizedZipCode === dbZipCode) {
        score += 20;
        console.log(`🔍 DEBUG: Postnummer match! +20 poäng`);
      }

      console.log(`🔍 DEBUG: Total poäng för lägenhet ${index + 1}: ${score}`);

      if (score > bestScore) {
        bestScore = score;
        bestMatch = { id: doc.id, data: data, score: score };
        console.log(`🔍 DEBUG: Ny bästa match! Poäng: ${score}`);
      }
    });

    // Returnera bästa match om den har tillräckligt höga poäng OCH postnummer matchar
    if (bestMatch && bestMatch.score >= 70) { // Lägenhetsnummer (50) + postnummer (20) måste matcha
      console.log('🔍 DEBUG: Bästa match hittad:', bestMatch);
      return { id: bestMatch.id, data: bestMatch.data };
    }
    
    console.log('🔍 DEBUG: Ingen tillräckligt bra match hittad. Bästa poäng:', bestScore);
    return null;
  } catch (error) {
    console.error('===findMatchingHousing===', error);
    return null;
  }
};

// Uppdatera lägenhet med ägarskap
export const updateHousingOwnership = async (
  housingId: string, 
  userInfo: { email: string; firstName: string; lastName: string; uid: string },
  associationId?: string
) => {
  try {
    const housingRef = doc(db, 'housing', housingId);
    const updateData: any = {
      ownedBy: userInfo.uid, // Endast kopplar till users collection
      updatedAt: new Date().toISOString()
    };
    
    if (associationId) {
      updateData.associationId = associationId;
    }
    
    await updateDoc(housingRef, updateData);
    
    return { success: true };
  } catch (error: unknown) {
    console.error('===updateHousingOwnership===', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// Skapa overifierad lägenhet
export const createUnverifiedHousing = async (
  personData: any,
  userInfo: { email: string; firstName: string; lastName: string; uid: string },
  associationId?: string
) => {
  try {
    const unverifiedData = {
      apartmentNumber: personData.Basic.ApartmentNumber,
      address: personData.Basic.Street,
      city: personData.Basic.City,
      zipCode: personData.Basic.ZipCode,
      ownedBy: userInfo.uid, // Kopplar till users collection
      associationId: associationId || '', // Koppling till associations collection
      status: "PENDING_VERIFICATION",
      createdAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(collection(db, 'unverifiedHousing'), unverifiedData);
    
    return { 
      success: true, 
      id: docRef.id, 
      data: { ...unverifiedData, id: docRef.id } 
    };
  } catch (error: unknown) {
    console.error('===createUnverifiedHousing===', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// Spara adressdata i housing collection (för framtida användning)
export const saveAddressToHousing = async (
  personData: any,
  userInfo: { email: string; firstName: string; lastName: string; uid: string },
  housingCooperative?: string,
  associationId?: string
) => {
  try {
    // Normalisera data för sökning
    const normalizedAddress = normalizeAddress(personData.Basic.Street);
    const normalizedApartmentNumber = normalizeApartmentNumber(personData.Basic.ApartmentNumber);
    const normalizedZipCode = normalizeZipCode(personData.Basic.ZipCode);

    // Hämta alla housing-dokument och filtrera på klientsidan
    // Detta undviker permissions-problem med composite queries
    const allHousingSnapshot = await getDocs(collection(db, 'housing'));

    let existingDoc: any = null;

    allHousingSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      const docNormalizedAddress = normalizeAddress(data.address || '');
      const docNormalizedApartmentNumber = normalizeApartmentNumber(data.apartmentNumber || '');
      const docNormalizedZipCode = normalizeZipCode(data.zipCode || '');

      if (
        docNormalizedAddress === normalizedAddress &&
        docNormalizedApartmentNumber === normalizedApartmentNumber &&
        docNormalizedZipCode === normalizedZipCode
      ) {
        existingDoc = { id: docSnap.id, data };
      }
    });

    if (existingDoc) {
      // Lägenheten finns redan - uppdatera ägarskap istället för att skapa ny
      const existingData = existingDoc.data;

      // Om lägenheten redan ägs av någon annan
      if (existingData.ownedBy && existingData.ownedBy !== userInfo.uid) {
        return {
          success: false,
          error: 'Denna lägenhet är redan kopplad till en annan användare'
        };
      }

      // Om lägenheten saknar ägare eller redan ägs av denna användare, uppdatera
      await updateDoc(doc(db, 'housing', existingDoc.id), {
        ownedBy: userInfo.uid,
        addressVerified: true,
        updatedAt: new Date().toISOString(),
        // Uppdatera associationId och housingCooperative om de finns
        ...(associationId && { associationId }),
        ...(housingCooperative && { housingCooperative })
      });

      return {
        success: true,
        id: existingDoc.id,
        data: { ...existingData, ownedBy: userInfo.uid, id: existingDoc.id }
      };
    }

    // Om lägenheten inte finns, skapa ny
    const addressData = {
      apartmentNumber: personData.Basic.ApartmentNumber,
      address: personData.Basic.Street,
      city: personData.Basic.City,
      zipCode: personData.Basic.ZipCode,
      ownedBy: userInfo.uid, // Kopplar till users collection
      housingCooperative: housingCooperative || '', // BRF-namn om matchning finns, annars tomt
      associationId: associationId || '', // Koppling till associations collection
      addressVerified: true, // Användaren har bekräftat adressen
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Lägg till normaliserade fält för matchning
      normalizedAddress,
      normalizedApartmentNumber,
      normalizedZipCode
    };

    const docRef = await addDoc(collection(db, 'housing'), addressData);

    return {
      success: true,
      id: docRef.id,
      data: { ...addressData, id: docRef.id }
    };
  } catch (error: unknown) {
    console.error('===saveAddressToHousing===', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// Kontrollera om användaren redan har en verifierad adress (med personnummer som backup)
export const checkUserHasVerifiedAddress = async (userId: string, personalNumber?: string) => {
  try {
    // console.log('🔍 checkUserHasVerifiedAddress: Söker efter verifierad adress för userId:', userId, 'personnummer:', personalNumber);
    
    // Försök först med userId
    try {
      const qUserId = query(
        collection(db, 'housing'), 
        where('ownedBy', '==', userId),
        where('addressVerified', '==', true)
      );
      
      const userIdSnapshot = await getDocs(qUserId);
      // console.log('🔍 checkUserHasVerifiedAddress: Antal dokument hittade med userId:', userIdSnapshot.size);
      
      if (!userIdSnapshot.empty) {
        const doc = userIdSnapshot.docs[0];
        const data = doc.data();
        // console.log('✅ checkUserHasVerifiedAddress: Verifierad adress hittad med userId:', data);
        return { 
          hasVerifiedAddress: true, 
          data: data,
          id: doc.id 
        };
      }
      
    } catch (permissionError: unknown) {
      // console.log('⚠️ checkUserHasVerifiedAddress: Permissions-fel vid userId-sökning:', permissionError instanceof Error ? permissionError.message : String(permissionError));
      // Fortsätt med personnummer-sökning som backup
    }
    
    // Om inget hittas med userId och vi har personnummer, försök med det
    if (personalNumber) {
      try {
        // console.log('🔍 checkUserHasVerifiedAddress: Söker med personnummer istället...');
        
        // Hitta användarens tidigare UID genom att söka i users collection
        const qUsers = query(
          collection(db, 'users'),
          where('personalNumber', '==', personalNumber)
        );
        
        const usersSnapshot = await getDocs(qUsers);
        // console.log('🔍 checkUserHasVerifiedAddress: Antal användare med personnummer:', usersSnapshot.size);
      
      if (!usersSnapshot.empty) {
        // Ta första användaren som matchar (ska bara finnas en)
        const userDoc = usersSnapshot.docs[0];
        const previousUserId = userDoc.id;
        // console.log('🔍 checkUserHasVerifiedAddress: Hittade tidigare userId:', previousUserId);
        
        // Sök efter housing med den tidigare UID:n
        const qPreviousUser = query(
          collection(db, 'housing'),
          where('ownedBy', '==', previousUserId),
          where('addressVerified', '==', true)
        );
        
        const previousUserSnapshot = await getDocs(qPreviousUser);
        // console.log('🔍 checkUserHasVerifiedAddress: Antal dokument med tidigare userId:', previousUserSnapshot.size);
        
        if (!previousUserSnapshot.empty) {
          const doc = previousUserSnapshot.docs[0];
          const data = doc.data();
          // console.log('✅ checkUserHasVerifiedAddress: Verifierad adress hittad med tidigare userId:', data);
          
          // Uppdatera housing-dokumentet med nya UID
          await updateDoc(doc.ref, { ownedBy: userId });
          // console.log('🔄 checkUserHasVerifiedAddress: Uppdaterade ownedBy till ny UID:', userId);
          
          return { 
            hasVerifiedAddress: true, 
            data: { ...data, ownedBy: userId },
            id: doc.id 
          };
        }
      }
      
      } catch (personalNumberError: unknown) {
        // console.log('⚠️ checkUserHasVerifiedAddress: Fel vid personnummer-sökning:', personalNumberError instanceof Error ? personalNumberError.message : String(personalNumberError));
      }
    }
    
    // console.log('❌ checkUserHasVerifiedAddress: Ingen verifierad adress hittad');
    return { hasVerifiedAddress: false };
  } catch (error: unknown) {
    console.error('===checkUserHasVerifiedAddress===', error);
    return { hasVerifiedAddress: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// Hämta alla föreningar från associations collection
export const getAllAssociations = async (): Promise<{ data?: any[]; errorMessage?: string }> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'associations'));
    const associations: any[] = [];

    querySnapshot.forEach((doc) => {
      associations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { data: associations.sort((a, b) => a.name.localeCompare(b.name)) };
  } catch (error) {
    console.error('===getAllAssociations===', error);
    return {
      errorMessage: 'Error when fetching associations...'
    };
  }
};

// Hämta alla lägenheter för en specifik förening (via associationId)
export const getApartmentsByAssociation = async (
  associationId: string
): Promise<{ data?: Housing[]; errorMessage?: string }> => {
  try {
    const q = query(
      collection(db, 'housing'), 
      where('associationId', '==', associationId)
    );
    const querySnapshot = await getDocs(q);

    const data: Housing[] = [];

    querySnapshot.forEach((doc) => {
      data.push(doc.data() as Housing);
    });

    return { data };
  } catch (error) {
    console.error('===getApartmentsByAssociation===', error);
    return {
      errorMessage: 'Error when fetching apartments...'
    };
  }
};

// Kontrollera om användaren har lägenheter kopplade till föreningar
export const checkUserHasAssociationApartments = async (uid: string): Promise<{
  hasAssociationApartments: boolean;
  apartmentsWithAssociation: Housing[];
  apartmentsWithoutAssociation: Housing[];
  hasPendingClaim: boolean;
}> => {
  try {
    // console.log('🔍 Checking user association apartments for UID:', uid);
    
    // Först kontrollera om användaren är BRF-admin med associationId
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      
      // Om användaren är BRF-admin med associationId, tillåt direkt
      if (userData.role === 'brfadmin' && userData.associationId && userData.associationId.trim() !== '') {
        console.log('🔑 BRF Admin with associationId detected, allowing access');
        return {
          hasAssociationApartments: true,
          apartmentsWithAssociation: [],
          apartmentsWithoutAssociation: [],
          hasPendingClaim: false
        };
      }
    }
    
    // För vanliga användare, kontrollera lägenheter.
    // Ett väntande anspråk saknar ownedBy tills Hyria godkänt det, så det måste
    // hämtas separat via claimedBy — annars ser flödet ut som att användaren
    // inte har någon lägenhet alls.
    const [ownedSnapshot, claimedSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'housing'), where('ownedBy', '==', uid))),
      getDocs(query(collection(db, 'housing'), where('claimedBy', '==', uid)))
    ]);

    const toHousing = (snapshot: typeof ownedSnapshot): Housing[] =>
      snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Housing & { id: string });

    return summarizeApartmentAccess({
      ownedApartments: toHousing(ownedSnapshot),
      claimedApartments: toHousing(claimedSnapshot)
    });
    
  } catch (error) {
    console.error('===checkUserHasAssociationApartments===', error);
    return {
      hasAssociationApartments: false,
      apartmentsWithAssociation: [],
      apartmentsWithoutAssociation: [],
      hasPendingClaim: false
    };
  }
};

// Sök i alla lägenheter (för profil-sökning)
export const searchAllHousing = async (searchTerm: string = ''): Promise<GetHousingInformationReturnType> => {
  try {
    // Hämta alla lägenheter först
    const querySnapshot = await getDocs(collection(db, 'housing'));

    let data: Housing[] = [];

    querySnapshot.forEach((doc) => {
      const housingData = doc.data() as Housing;
      data.push({ ...housingData, id: doc.id } as any);
    });

    // Filtrera baserat på sökterm på frontend-sidan
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      data = data.filter(housing => {
        const searchableText = `${housing.address || ''} ${housing.apartmentNumber || ''} ${housing.housingCooperative || ''} ${housing.associationId || ''} ${housing.city || ''}`.toLowerCase();
        return searchableText.includes(lowerSearchTerm);
      });
    }

    return { data };
  } catch (error) {
    console.error('===searchAllHousing===', error);
    return {
      errorMessage: 'Error when searching housing...'
    };
  }
};

// Lägg till lägenhet till användarens profil
export const addHousingToUser = async (
  userId: string,
  housingData: {
    address: string;
    city: string;
    apartmentNumber: string;
    housingCooperative?: string;
    associationId?: string;
    zipCode?: string;
  }
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    // Normalisera data för sökning
    const normalizedAddress = normalizeAddress(housingData.address);
    const normalizedApartmentNumber = normalizeApartmentNumber(housingData.apartmentNumber);
    const normalizedZipCode = normalizeZipCode(housingData.zipCode || '');

    // Hämta alla housing-dokument och filtrera på klientsidan
    // Detta undviker permissions-problem med composite queries
    const allHousingSnapshot = await getDocs(collection(db, 'housing'));

    let existingDoc: any = null;

    allHousingSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      const docNormalizedAddress = normalizeAddress(data.address || '');
      const docNormalizedApartmentNumber = normalizeApartmentNumber(data.apartmentNumber || '');
      const docNormalizedZipCode = normalizeZipCode(data.zipCode || '');

      if (
        docNormalizedAddress === normalizedAddress &&
        docNormalizedApartmentNumber === normalizedApartmentNumber &&
        docNormalizedZipCode === normalizedZipCode
      ) {
        existingDoc = { id: docSnap.id, data };
      }
    });

    // Hämta inloggad users namn för ev. auto-match mot members[]
    const userSnap = await getDoc(doc(db, 'users', userId));
    const userData = userSnap.exists() ? userSnap.data() : {};
    const userFirst = (userData.firstName || '').toLowerCase().trim();
    const userLast = (userData.lastName || '').toLowerCase().trim();

    /**
     * Smart auto-match: godkänn direkt om EXAKT EN member-rad har samma namn
     * som inloggad user OCH housing-docet är fritt eller redan tillhör user.
     * Annars sätts PENDING_APPROVAL och Hyria-admin granskar manuellt.
     */
    const autoApproveAgainstMembers = (members: any[]): boolean => {
      if (!Array.isArray(members) || members.length === 0) return false;
      if (!userFirst || !userLast) return false;
      const matches = members.filter((m: any) =>
        (m?.firstName || '').toLowerCase().trim() === userFirst &&
        (m?.lastName || '').toLowerCase().trim() === userLast
      );
      return matches.length === 1;
    };

    const nowIso = new Date().toISOString();

    if (existingDoc) {
      // Lägenheten finns redan - hantera ägarskap-claim
      const existingData = existingDoc.data;

      // Om lägenheten redan ägs av någon annan
      if (existingData.ownedBy && existingData.ownedBy !== userId) {
        return {
          success: false,
          error: 'Denna lägenhet är redan kopplad till en annan användare'
        };
      }

      // Avgör om det är en auto-match eller måste pending-grans-kas av Hyria
      const sameUserOrEmpty = !existingData.ownedBy || existingData.ownedBy === userId;
      const matchesMembers = autoApproveAgainstMembers(existingData.members);
      const canAutoApprove = sameUserOrEmpty && matchesMembers;

      const baseUpdate: Record<string, any> = {
        claimedBy: userId,
        claimedAt: nowIso,
        updatedAt: nowIso,
        // Fyll bara i föreningen när dokumentet saknar den. Ett anspråk ska
        // aldrig kunna flytta en lägenhet till en annan förening.
        ...(housingData.associationId && !existingData.associationId && {
          associationId: housingData.associationId
        }),
        ...(housingData.housingCooperative && !existingData.housingCooperative && {
          housingCooperative: housingData.housingCooperative
        })
      };

      if (canAutoApprove) {
        baseUpdate.ownedBy = userId;
        baseUpdate.addressVerified = true;
        baseUpdate.ownershipStatus = 'APPROVED';
        baseUpdate.claimSource = 'auto_match';
        baseUpdate.approvedBy = 'system_auto_match';
        baseUpdate.approvedAt = nowIso;
      } else {
        // OBS: ownedBy sätts INTE förrän Hyria godkänt — annars kan användaren
        // skapa ansökningar för housing de inte är verifierade ägare av
        baseUpdate.ownershipStatus = 'PENDING_APPROVAL';
        baseUpdate.claimSource = 'manual_pending';
      }

      await updateDoc(doc(db, 'housing', existingDoc.id), baseUpdate);

      // Vid pending-claim — notifiera Hyria-admin via mejl (fire-and-forget).
      if (baseUpdate.ownershipStatus === 'PENDING_APPROVAL') {
        notifyAdminsOfPendingClaim(existingDoc.id);
      }

      return {
        success: true,
        data: {
          id: existingDoc.id,
          ...existingData,
          ...baseUpdate
        }
      };
    }

    // Lägenheten finns inte sedan tidigare — skapa ny som PENDING.
    // Eftersom det inte finns något medlemsregister att matcha mot kan vi
    // aldrig auto-godkänna — alla nya housing-docs börjar som PENDING_APPROVAL.
    const newHousing = {
      ...housingData,
      addressVerified: false,
      ownershipStatus: 'PENDING_APPROVAL' as const,
      claimedBy: userId,
      claimedAt: nowIso,
      claimSource: 'manual_pending' as const,
      createdAt: nowIso,
      updatedAt: nowIso,
      normalizedAddress,
      normalizedApartmentNumber,
      normalizedZipCode
    };

    const docRef = await addDoc(collection(db, 'housing'), newHousing);

    // Ny housing skapas alltid som PENDING — notifiera Hyria-admin.
    notifyAdminsOfPendingClaim(docRef.id);

    return {
      success: true,
      data: { id: docRef.id, ...newHousing }
    };
  } catch (error: unknown) {
    console.error('===addHousingToUser===', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

/**
 * Fire-and-forget: mejla alla Hyria-admins om ett nytt pending claim att granska.
 * Vi awaitar INTE detta — användarens claim-flöde ska inte blockeras av en
 * eventuell mejl-fail eller långsam Cloud Function.
 */
const notifyAdminsOfPendingClaim = (housingId: string): void => {
  try {
    const fn = httpsCallable(functions, 'notifyAdminsNewClaim');
    fn({ housingId }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[notifyAdminsNewClaim] non-blocking failure:', err);
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[notifyAdminsNewClaim] could not invoke:', err);
  }
};

// Ta bort ägarskap från lägenhet (behåller dokumentet men tar bort kopplingen)
export const deleteHousing = async (
  housingId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('🗑️ Removing ownership from housing document:', housingId);

    const housingRef = doc(db, 'housing', housingId);

    // Ta bort ägarskap istället för att radera hela dokumentet
    await updateDoc(housingRef, {
      ownedBy: null,
      addressVerified: false,
      updatedAt: new Date().toISOString()
    });

    console.log('✅ Ownership removed from housing document successfully');
    return { success: true };
  } catch (error: unknown) {
    console.error('===deleteHousing===', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

/**
 * Clean up member registry data for an association
 * Only accessible to Hyria Admin users
 * @param associationId - The ID of the association
 * @param preview - If true, returns what would be deleted without actually deleting
 */
export const cleanupMemberRegistry = async (
  associationId: string,
  preview: boolean = false
): Promise<{
  success: boolean;
  deletedCount?: number;
  apartmentKeys?: string[];
  error?: string;
}> => {
  try {
    const cleanupFunction = httpsCallable(functions, 'cleanupMemberRegistry');
    const result = await cleanupFunction({ associationId, preview });

    return result.data as {
      success: boolean;
      deletedCount?: number;
      apartmentKeys?: string[];
      error?: string;
    };
  } catch (error) {
    console.error('Error cleaning up member registry:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};
